"use client";

import { Fragment, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { api } from "@/lib/api";
import { DownloadButton } from "@/components/download";
import { fmtKrw, fmtQty, fmtUsdt } from "@/lib/format";

interface Dashboard {
  balance: {
    company_holdings: string;
    foundation_receivable: string;
    prepaid_balance: string;
    total_outstanding: string;
    company_operating: string;
    loan_balance_krw: string;
    surplus_deficit: string;
    procurement_entitlement_gap: string;
    unpaid_company_cta: string;
    unreceived_foundation_cta: string;
    received_from_foundation_cta: string;
    company_remaining_cta: string;
    foundation_remaining_cta: string;
    company_initial_cta: string;
    reward_in_total: string;
    company_outstanding_cta: string;
    received_from_company_cta: string;
    provisional_foundation_cta: string;
    provisional_company_cta: string;
  };
  indicators: {
    pending_approvals: number;
    recon_needed: number;
    draft_total: number;
    draft_reviewed: number;
  };
  sales: {
    cash_krw_total: string;
    usdt_total: string;
    usdt_krw_total: string;
    monthly: { month: string; cash_krw: string; usdt: string; usdt_krw: string }[];
  };
  sales_by_funding_source: {
    foundation: { qty: string; cash_krw: string; usdt: string; usdt_krw: string };
    company: { qty: string; cash_krw: string; usdt: string; usdt_krw: string };
  };
  nodes: { node_id: number; node_name: string; status: string; total_qty: string }[];
  loans: {
    creditors: {
      member_id: number; name: string; principal_total: string;
      repaid_total: string; balance: string;
    }[];
    repayments_in_progress: number;
  };
  top_outstanding: { member_id: number; name: string; outstanding: string }[];
  prepaid_members: { member_id: number; name: string; prepaid_balance: string }[];
}

function Card({
  title,
  value,
  sub,
  subLines,
  subEmphasis,
  tone = "default",
  href,
}: {
  title: string;
  value: string;
  sub?: string;
  // 라벨 길이가 달라도 "-"와 수량 위치가 세로로 맞도록 그리드로 렌더링 (예: 잔액/가지급 — v1.4.26)
  // 줄마다 강조 여부를 따로 줄 수 있다(예: 잔액은 눈에 띄게, 가지급은 보조 정보로)
  subLines?: { label: string; value: string; emphasis?: boolean }[];
  subEmphasis?: boolean;
  tone?: "default" | "asset" | "debt" | "krw";
  href?: string;
}) {
  const tones = {
    default: "border-slate-300",
    asset: "border-emerald-300",
    debt: "border-rose-300",
    krw: "border-amber-300",
  };
  const body = (
    <div className={`h-full rounded-lg border bg-white p-4 shadow-sm ${tones[tone]} ${
      href ? "cursor-pointer transition hover:shadow-md hover:ring-1 hover:ring-slate-300" : ""}`}>
      <p className="text-xs text-slate-500">{title}</p>
      <p className="mt-1 text-lg font-bold tabular-nums">{value}</p>
      {sub && (
        subEmphasis ? (
          <p className="mt-1 whitespace-pre-line text-base font-extrabold tabular-nums text-emerald-700">{sub}</p>
        ) : sub.includes("\n") ? (
          // 첫 줄(총액 등 합계)은 굵게, 나머지 줄(세부 내역)은 들여쓰기로 구분 (v1.4.40)
          <div className="mt-0.5 text-xs">
            {sub.split("\n").map((line, i) => (
              <p key={i} className={i === 0 ? "font-semibold text-slate-600" : "pl-3 text-slate-400"}>
                {line}
              </p>
            ))}
          </div>
        ) : (
          <p className="mt-0.5 whitespace-pre-line text-xs text-slate-400">{sub}</p>
        )
      )}
      {subLines && subLines.length > 0 && (
        <div className="mt-1 grid grid-cols-[auto_1fr] items-baseline gap-x-1">
          {subLines.map((l) => (
            <Fragment key={l.label}>
              <span className={l.emphasis ? "text-base font-extrabold text-emerald-700" : "text-xs text-slate-400"}>
                {l.label}
              </span>
              <span className={l.emphasis
                ? "text-base font-extrabold tabular-nums text-emerald-700"
                : "text-xs tabular-nums text-slate-400"}>
                : {l.value} CTA
              </span>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
  return href ? <Link href={href} className="block h-full">{body}</Link> : body;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold text-slate-700">{title}</h2>
      {children}
    </section>
  );
}

// DB 값(active/draining/closed)은 그대로, 화면 표시 문구만 nodes/page.tsx의 배지 표기와 동일하게 조정
const NODE_STATUS: Record<string, string> = {
  active: "운영중", draining: "물량 정리중", closed: "폐쇄됨",
};

export default function DashboardPage() {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 판매대금 연도별 아코디언 — 한 번에 하나만 펼침, 최근 연도가 기본으로 펼쳐진다
  const [expandedYear, setExpandedYear] = useState<string | null>(null);
  const [loansExpanded, setLoansExpanded] = useState(false);

  useEffect(() => {
    api<Dashboard>("/api/dashboard").then((d) => {
      setData(d);
      const years = [...new Set(d.sales.monthly.map((r) => r.month.slice(0, 4)))].sort().reverse();
      setExpandedYear(years[0] ?? null);
    }).catch((e) => setError(e.message));
  }, []);

  const salesByYear = useMemo(() => {
    if (!data) return [];
    const groups = new Map<string, { month: string; cash_krw: string; usdt: string; usdt_krw: string }[]>();
    for (const r of data.sales.monthly) {
      const year = r.month.slice(0, 4);
      if (!groups.has(year)) groups.set(year, []);
      groups.get(year)!.push(r);
    }
    return [...groups.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([year, rows]) => ({
        // 연도 내림차순은 그대로, 월은 오름차순(백엔드 ORDER BY 1)으로 들어오므로 화면에서만 뒤집는다
        year, rows: [...rows].reverse(),
        cashTotal: rows.reduce((s, r) => s + Number(r.cash_krw), 0),
        usdtTotal: rows.reduce((s, r) => s + Number(r.usdt), 0),
        usdtKrwTotal: rows.reduce((s, r) => s + Number(r.usdt_krw), 0),
      }));
  }, [data]);

  if (error) return <p className="text-red-600">{error}</p>;
  if (!data) return <p className="text-slate-500">불러오는 중…</p>;

  const { balance: b, indicators: ind, sales_by_funding_source: sfs } = data;
  const loanTotals = data.loans.creditors.reduce(
    (t, c) => ({
      principal: t.principal + Number(c.principal_total),
      repaid: t.repaid + Number(c.repaid_total),
      balance: t.balance + Number(c.balance),
    }),
    { principal: 0, repaid: 0, balance: 0 },
  );
  // 회사 총 잔액 = 재단 잔액 + 회사 잔액 (각 카드에도 "잔액" 서브라인으로 이미 표시 중인 값의 합)
  const totalRemainingCta = Number(b.foundation_remaining_cta) + Number(b.company_remaining_cta);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <DownloadButton href="/api/exports/dashboard.xlsx" />
      </div>
      {/* 상단 요약 (v1.4.19) — 1줄: 재단판매·재단미지급·회사판매·회사미지급, 2줄: 차입금·승인대기·대사필요
          두 줄을 별도 그리드로 나눠 1줄 4개 박스가 항상 같은 높이로 맞춰지도록 한다 (v1.4.26) */}
      <div className="grid grid-cols-2 items-stretch gap-3 md:grid-cols-4">
        <Card title="재단 판매" value={`${fmtQty(sfs.foundation.qty)} CTA`} tone="asset"
              subLines={[{ label: "지급", value: fmtQty(b.received_from_foundation_cta), emphasis: true }]}
              sub={`총액: ${fmtKrw(Number(sfs.foundation.cash_krw) + Number(sfs.foundation.usdt_krw))}\n`
                + `현금: ${fmtKrw(sfs.foundation.cash_krw)}\nUSDT: ${fmtUsdt(sfs.foundation.usdt)}`
                + (Number(sfs.foundation.usdt) > 0 ? ` (환산 ${fmtKrw(sfs.foundation.usdt_krw)})` : "")}
              href="/transactions?tx_type=SALE_ENTITLEMENT,PRICE_ADJUSTMENT,REFUND_ENTITLEMENT&funding_source=FOUNDATION&status=approved" />
        <Card title="재단 미지급 수량" value={`${fmtQty(b.unreceived_foundation_cta)} CTA`} tone="debt"
              subLines={[
                ...(Number(b.provisional_foundation_cta) > 0
                  ? [{ label: "가지급", value: fmtQty(b.provisional_foundation_cta) }] : []),
                { label: "잔액", value: fmtQty(b.foundation_remaining_cta), emphasis: true },
              ]}
              href="/transactions?entitlement_only=true&funding_source=FOUNDATION&status=approved" />
        <Card title={`회사 판매 (초기물량 ${fmtQty(b.company_initial_cta)} CTA${
                Number(b.reward_in_total) > 0 ? ` + 보상 ${fmtQty(b.reward_in_total)}` : ""})`}
              value={`${fmtQty(sfs.company.qty)} CTA`} tone="asset"
              subLines={[{ label: "지급", value: fmtQty(b.received_from_company_cta), emphasis: true }]}
              sub={`총액: ${fmtKrw(Number(sfs.company.cash_krw) + Number(sfs.company.usdt_krw))}\n`
                + `현금: ${fmtKrw(sfs.company.cash_krw)}\nUSDT: ${fmtUsdt(sfs.company.usdt)}`
                + (Number(sfs.company.usdt) > 0 ? ` (환산 ${fmtKrw(sfs.company.usdt_krw)})` : "")}
              href="/transactions?tx_type=SALE_ENTITLEMENT,PRICE_ADJUSTMENT,REFUND_ENTITLEMENT&funding_source=COMPANY&status=approved" />
        <Card title="회사 미지급 수량" value={`${fmtQty(b.company_outstanding_cta)} CTA`} tone="debt"
              subLines={[
                ...(Number(b.provisional_company_cta) > 0
                  ? [{ label: "가지급", value: fmtQty(b.provisional_company_cta) }] : []),
                { label: "잔액", value: fmtQty(b.company_remaining_cta), emphasis: true },
              ]}
              href="/transactions?entitlement_only=true&funding_source=COMPANY&status=approved" />
      </div>

      <div className="grid grid-cols-2 items-stretch gap-3 md:grid-cols-4">
        <Card title="회사 총 잔액" value={`${fmtQty(totalRemainingCta)} CTA`} tone="asset"
              subLines={[
                { label: "재단 잔액", value: fmtQty(b.foundation_remaining_cta) },
                { label: "회사 잔액", value: fmtQty(b.company_remaining_cta) },
              ]} />
        <Card title="차입금 잔액" value={fmtKrw(loanTotals.balance)} tone="krw"
              sub={`원금 ${fmtKrw(loanTotals.principal)} · 상환액 ${fmtKrw(loanTotals.repaid)}`}
              href="/loans" />
        <Card title="승인 대기" value={`${ind.pending_approvals}건`} />
        <Card title="대사 필요 🔴" value={`${ind.recon_needed}건`} href="/reconciliation" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="판매 대금">
          <div className="mb-3 flex gap-6 text-sm">
            <span>현금 <b className="tabular-nums">{fmtKrw(data.sales.cash_krw_total)}</b></span>
            <span>USDT <b className="tabular-nums">{fmtUsdt(data.sales.usdt_total)}</b>
              {Number(data.sales.usdt_total) > 0 && (
                <span className="text-slate-400"> (환산 {fmtKrw(data.sales.usdt_krw_total)})</span>
              )}
            </span>
          </div>
          <div className="space-y-1">
            {salesByYear.map((y) => {
              const open = expandedYear === y.year;
              return (
                <div key={y.year} className="rounded border border-slate-100">
                  <button
                    onClick={() => setExpandedYear(open ? null : y.year)}
                    className="flex w-full items-center justify-between px-2 py-1.5 text-sm hover:bg-slate-50"
                  >
                    <span className="font-medium">
                      {y.year}년 <span className="ml-1 text-slate-400">{open ? "▾" : "▸"}</span>
                    </span>
                    <span className="tabular-nums text-slate-500">
                      현금 {fmtKrw(y.cashTotal)} · USDT {fmtUsdt(y.usdtTotal)}
                      {y.usdtTotal > 0 && ` (환산 ${fmtKrw(y.usdtKrwTotal)})`}
                    </span>
                  </button>
                  {open && (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-t border-b bg-slate-50 text-left text-xs text-slate-500">
                          <th className="py-1 pl-2">월</th><th className="text-right">현금</th>
                          <th className="text-right">USDT</th>
                          <th className="text-right pr-2">USDT 환산액</th>
                        </tr>
                      </thead>
                      <tbody>
                        {y.rows.map((r) => (
                          <tr key={r.month} className="border-b border-slate-100">
                            <td className="py-1 pl-2">{r.month}</td>
                            <td className="text-right tabular-nums">{fmtKrw(r.cash_krw)}</td>
                            <td className="text-right tabular-nums">{fmtUsdt(r.usdt)}</td>
                            <td className="text-right tabular-nums pr-2">
                              {Number(r.usdt) > 0 ? fmtKrw(r.usdt_krw) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
            {salesByYear.length === 0 && <p className="text-sm text-slate-400">판매 기록 없음</p>}
          </div>
        </Section>

        <Section title="미지급 상위 / 선지급 잔액">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="mb-1 text-xs font-medium text-rose-600">미지급 상위</p>
              <ul>
                {data.top_outstanding.map((m) => (
                  <li key={m.member_id} className="flex justify-between border-b border-slate-100 py-1">
                    <span>{m.name}</span>
                    <span className="tabular-nums">{fmtQty(m.outstanding)}</span>
                  </li>
                ))}
                {data.top_outstanding.length === 0 && <li className="text-slate-400">없음</li>}
              </ul>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-sky-600">선지급 잔액 🔵</p>
              <ul>
                {data.prepaid_members.map((m) => (
                  <li key={m.member_id} className="flex justify-between border-b border-slate-100 py-1">
                    <span>{m.name}</span>
                    <span className="tabular-nums">{fmtQty(m.prepaid_balance)}</span>
                  </li>
                ))}
                {data.prepaid_members.length === 0 && <li className="text-slate-400">없음</li>}
              </ul>
            </div>
          </div>
        </Section>

        <Section title={`차입금 현황 (상환 진행 중 ${data.loans.repayments_in_progress}건)`}>
          <button onClick={() => setLoansExpanded((v) => !v)}
                  className="mb-2 flex w-full items-center justify-between rounded bg-slate-50 px-2 py-1.5 text-sm hover:bg-slate-100">
            <span className="font-medium">
              합계 <span className="ml-1 text-slate-400">{loansExpanded ? "▾" : "▸"}</span>
            </span>
            <span className="tabular-nums text-slate-600">
              원금 {fmtKrw(data.loans.creditors.reduce((s, c) => s + Number(c.principal_total), 0))}
              {" · "}상환액 {fmtKrw(data.loans.creditors.reduce((s, c) => s + Number(c.repaid_total), 0))}
              {" · "}잔액{" "}
              <b>{fmtKrw(data.loans.creditors.reduce((s, c) => s + Number(c.balance), 0))}</b>
            </span>
          </button>
          {loansExpanded && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-slate-500">
                  <th className="py-1">채권자</th><th className="text-right">원금</th>
                  <th className="text-right">상환액</th><th className="text-right">잔액</th>
                </tr>
              </thead>
              <tbody>
                {data.loans.creditors.map((c) => (
                  <tr key={c.member_id} className="border-b border-slate-100">
                    <td className="py-1">{c.name}</td>
                    <td className="text-right tabular-nums">{fmtKrw(c.principal_total)}</td>
                    <td className="text-right tabular-nums">{fmtKrw(c.repaid_total)}</td>
                    <td className="text-right font-semibold tabular-nums">{fmtKrw(c.balance)}</td>
                  </tr>
                ))}
                {data.loans.creditors.length === 0 && (
                  <tr><td colSpan={4} className="py-2 text-slate-400">차입금 없음</td></tr>
                )}
              </tbody>
            </table>
          )}
        </Section>

        <Section title="노드 현황">
          <ul className="space-y-2">
            {data.nodes.map((n) => {
              const max = Math.max(...data.nodes.map((x) => Number(x.total_qty)), 1);
              return (
                <li key={n.node_id} className="text-sm">
                  <div className="flex justify-between">
                    <span>
                      {n.node_name}
                      <span className="ml-2 text-xs text-slate-400">{NODE_STATUS[n.status]}</span>
                    </span>
                    <span className="tabular-nums">{fmtQty(n.total_qty)}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded bg-slate-100">
                    <div
                      className="h-1.5 rounded bg-slate-600"
                      style={{ width: `${(Number(n.total_qty) / max) * 100}%` }}
                    />
                  </div>
                </li>
              );
            })}
            {data.nodes.length === 0 && <li className="text-sm text-slate-400">노드 없음</li>}
          </ul>
        </Section>
      </div>
    </div>
  );
}
